import styled from 'styled-components';
import { Close } from '@mui/icons-material';
import { Snackbar as SnackbarBase, Theme, Typography } from '@mui/material';
import { Flex } from '../Flex';
import IconButton from '../IconButton';
import { componentToken } from '../../style/utils';

export type SnackbarVariant = 'success' | 'warning' | 'error' | 'info';

export type SnackbarAction = { text: string; cb: () => void };

const toastVariant = (
  theme: Theme,
  $variant?: SnackbarVariant,
): SnackbarVariant | undefined =>
  $variant ??
  (theme.tokens.components.snackbar.infoByDefault ? 'info' : undefined);

const SnackbarStyled = styled(SnackbarBase)<{ $variant?: SnackbarVariant }>`
  & .MuiSnackbarContent-message {
    color: ${({ $variant, theme }) => {
      const variant = toastVariant(theme, $variant);
      return variant === 'success'
        ? theme.tokens.components.snackbar.message.successColor
        : variant === 'warning'
        ? theme.tokens.components.snackbar.message.warningColor
        : variant === 'error'
        ? theme.tokens.components.snackbar.message.errorColor
        : variant === 'info'
        ? theme.tokens.components.snackbar.message.infoColor
        : 'auto';
    }};
    max-width: 306px;
  }

  ${({ $variant, theme }) => {
    const variant = toastVariant(theme, $variant);
    const toast = variant
      ? theme.tokens.components.snackbar.toast[variant]
      : undefined;
    return (
      toast?.background &&
      `
      .MuiSnackbarContent-root {
        background: ${toast.background};
        border: 1px solid ${toast.border};
        box-shadow: ${toast.shadow};
        backdrop-filter: ${theme.tokens.components.snackbar.backdropFilter};
      }

      .MuiSnackbarContent-action .MuiTypography-root {
        color: ${toast.text};
      }
    `
    );
  }}
`;

const ActionButton = styled(Typography)`
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${componentToken('snackbar.actionButton.color')};
`;

type Props = {
  open: boolean;
  variant?: SnackbarVariant;
  message?: string;
  duration?: number;
  actions?: SnackbarAction[];
  autoHide?: boolean;
  onClose: () => void;
};

const Snackbar = ({
  open,
  variant,
  message,
  duration,
  actions,
  autoHide = true,
  onClose,
}: Props) => (
  <SnackbarStyled
    $variant={variant}
    open={open}
    autoHideDuration={autoHide ? duration ?? 6000 : null}
    message={message}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    onClose={onClose}
    ClickAwayListenerProps={{ mouseEvent: false }}
    action={
      <Flex sx={{ pl: 1, gap: 3, alignItems: 'center' }}>
        {actions?.map((action) => (
          <ActionButton key={action.text} onClick={() => action.cb()}>
            {action.text}
          </ActionButton>
        ))}
        <IconButton sx={{ color: 'inherit' }} onClick={onClose}>
          <Close />
        </IconButton>
      </Flex>
    }
  />
);

export default Snackbar;
